import { APIFY_MIN_SCRAPE_COST_MICRODOLLARS } from "@degaus/shared/constants";
import { getAllModelPricing } from "@degaus/shared/models/pricing";

export type ModelPricing = {
  unit: string;
  variant?: string;
  unitPriceMicrodollars: number;
  unitPriceUsd: string;
  credits: number;
  note?: string;
};

export type ReferencePricingEntry = {
  unit: string;
  unitPriceMicrodollars: number;
  variant?: string;
};

export type ReferencePricing = {
  provider: string;
  url?: string;
  entries: ReferencePricingEntry[];
};

export type PricingModel = {
  key: string;
  label: string;
  description: string;
  stepType: string;
  pricing: ModelPricing[];
  referencePricing?: ReferencePricing;
};

export type EditorPricing = {
  type: string;
  label: string;
  pricing: {
    unit: string;
    unitPriceMicrodollars: number;
    unitPriceUsd: string;
    credits: number;
  };
};

export type OpenRouterModel = {
  model: string;
  inputPerMillionTokensMicrodollars: number;
  outputPerMillionTokensMicrodollars: number;
  inputPerMillionTokensUsd: string;
  outputPerMillionTokensUsd: string;
  inputCreditsPerMillionTokens: number;
  outputCreditsPerMillionTokens: number;
};

export type PricingData = {
  models: PricingModel[];
  editor: EditorPricing;
  openRouterModels?: OpenRouterModel[];
};

export function getPricingData(): PricingData {
  const raw = getAllModelPricing();

  const scrapingEntries: PricingModel[] = [
    {
      key: "scrape-ig-profile",
      label: "Instagram Profile Scrape",
      description:
        "Scrape profile info, posts, and reels from an Instagram account",
      stepType: "scraping",
      pricing: [
        {
          unit: "per_profile",
          unitPriceMicrodollars: 50_000,
          unitPriceUsd: "$0.050000",
          credits: 50_000,
          note: "Typical cost for profile + posts + reels.",
        },
      ],
      referencePricing: {
        provider: "Apify",
        url: "https://apify.com/pricing",
        entries: [{ unit: "per_profile", unitPriceMicrodollars: 50_000 }],
      },
    },
    {
      key: "scrape-ig-reel",
      label: "Instagram Reel Download",
      description: "Download a single Instagram reel by URL with full metadata",
      stepType: "scraping",
      pricing: [
        {
          unit: "per_reel",
          unitPriceMicrodollars: APIFY_MIN_SCRAPE_COST_MICRODOLLARS,
          unitPriceUsd: `$${(APIFY_MIN_SCRAPE_COST_MICRODOLLARS / 1_000_000).toFixed(6)}`,
          credits: APIFY_MIN_SCRAPE_COST_MICRODOLLARS,
        },
      ],
      referencePricing: {
        provider: "Apify",
        url: "https://apify.com/pricing",
        entries: [
          {
            unit: "per_reel",
            unitPriceMicrodollars: APIFY_MIN_SCRAPE_COST_MICRODOLLARS,
          },
        ],
      },
    },
    {
      key: "scrape-tiktok-profile",
      label: "TikTok Profile Scrape",
      description: "Scrape profile info and videos from a TikTok account",
      stepType: "scraping",
      pricing: [
        {
          unit: "per_profile",
          unitPriceMicrodollars: 40_000,
          unitPriceUsd: "$0.040000",
          credits: 40_000,
          note: "Typical cost for profile + up to 20 videos.",
        },
      ],
      referencePricing: {
        provider: "Apify",
        url: "https://apify.com/pricing",
        entries: [{ unit: "per_profile", unitPriceMicrodollars: 40_000 }],
      },
    },
    {
      key: "scrape-meta-ads",
      label: "Meta Ads Library Search",
      description:
        "Search the Meta Ads Library by brand or keyword across Facebook and Instagram",
      stepType: "scraping",
      pricing: [
        {
          unit: "per_search",
          unitPriceMicrodollars: 15_000,
          unitPriceUsd: "$0.015000",
          credits: 15_000,
          note: "Typical cost per search (up to 100 ads).",
        },
      ],
      referencePricing: {
        provider: "Apify",
        url: "https://apify.com/pricing",
        entries: [{ unit: "per_search", unitPriceMicrodollars: 15_000 }],
      },
    },
  ];

  return {
    models: [
      ...raw.models.map(({ model, ...rest }) => ({
        key: model,
        ...rest,
        label: MODEL_LABELS[model] ?? rest.label,
      })),
      ...scrapingEntries,
    ],
    editor: raw.editor,
  };
}

// Display names for /models where the catalog label is ambiguous (four
// "ElevenLabs" rows) or reads like an internal tier name.
const MODEL_LABELS: Record<string, string> = {
  "elevenlabs-tts": "ElevenLabs Text to Speech",
  "elevenlabs-stt": "ElevenLabs Speech to Text",
  "elevenlabs-dialogue": "ElevenLabs Dialogue",
  "elevenlabs-extract-timestamps": "ElevenLabs Word Timestamps",
  "seedance-2": "Seedance 2",
  "bria-video-background-removal": "Video Background Removal, Premium",
  "veed-video-background-removal": "Video Background Removal, Standard",
  "veed-video-background-removal-fast": "Video Background Removal, Fast",
};

export function getCategory(stepType: string): string {
  if (stepType === "image") return "image";
  if (stepType === "video") return "video";
  if (stepType === "scraping") return "scraping";
  if (["tts", "stt", "dialogue", "alignment", "music"].includes(stepType))
    return "audio";
  return "text";
}

export function formatPrice(microdollars: number): string {
  const dollars = microdollars / 1_000_000;
  if (dollars >= 0.01) return `$${dollars.toFixed(2)}`;
  if (dollars >= 0.001) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(6)}`;
}

export function dedupePrice(
  pricing: PricingModel["pricing"],
): PricingModel["pricing"] {
  const seen = new Set<string>();
  return pricing.filter((tier) => {
    const key = `${tier.unitPriceMicrodollars}:${tier.unit}:${tier.variant ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function formatUnit(unit: string): string {
  const map: Record<string, string> = {
    per_image: "per image",
    per_second: "per second",
    per_character: "per character",
    per_generation: "per generation",
    per_render: "per render",
    per_profile: "per profile",
    per_reel: "per reel",
    per_search: "per search",
  };
  return map[unit] ?? unit.replace(/_/g, " ");
}
